import $$dropdowns from 'models/dropdowns';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Frame } from './StyledTemplates';

import { createId, getElementClassPath } from 'utils/common-helper';

import useComponentSize from 'hooks/useComponentSize';
import useOnClickOutside from 'hooks/useOnClickOutside';

export const DropdownStyles = {
    Wrapper: styled(Frame)`
        position: relative;

        ${({ extra }) => extra}
    `,
    Toggle: styled(Frame).attrs((props) => {
        return {
            ...props,
            // className: `dropdown-toggle`,
        };
    })`
        cursor: pointer;
    `,
    Menu: styled(Frame).attrs((props) => ({
        ...props,
        className: 'dropdown-menu',
    }))<{ ref?: any; visible?: boolean; toggleSize?: any; direction?: string; extra?: string }>`
        flex: 1;
        /* overflow: auto; */
        /* width: inherit; */

        position: absolute;
        top: 100%;
        left: 100%;
        z-index: 2;
        visibility: ${({ visible }: any) => (visible ? `visible` : `hidden`)};
        opacity: ${({ visible }: any) => (visible ? 1 : 0)};
        transform: translate(
            -100%,
            ${({ visible, direction = `down` }: any) => (visible ? ({ down: 5, up: -5 } as any)?.[direction] : -15)}px
        );

        padding: 10px;
        border-radius: 9px;
        background: ${({ theme }) => theme.background.primary};
        border: 1px solid #dfdfdf;
        box-sizing: border-box;
        filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.05));
        width: max-content;

        ${({ extra }: any) => extra}
    `,
    ScrollWrapper: styled.div<{ extra?: string }>`
        width: 100%;
        display: flex;
        flex-direction: column;
        min-height: min-content;
        max-height: 400px;

        ${({ extra }: any) => extra}
    `,
    Option: styled(Frame)`
        width: 100%;
        align-items: flex-start;
        margin: 5px 0;
        cursor: pointer;
        &:hover {
            transform: translateX(2px);
        }
    `,
};

type DropdownProps = {
    className?: string;
    toggle: React.ReactNode;
    menu: React.ReactNode;
    toggleStyles?: string;
    menuStyles?: string;
    toggleProps?: any;
    menuProps?: any;
    wrapperStyles?: string;
    id?: string;
    closeOnChildrenClick?: boolean;
    callable?: boolean;
    alloweToClickClassesList?: string[];
    onClickOutside?: (e: any) => void;
};

const Dropdown = (props: DropdownProps) => {
    const {
        className = '',
        toggle,
        menu,
        toggleStyles = ``,
        menuStyles = ``,
        toggleProps = {},
        menuProps = {},
        wrapperStyles = ``,
        id = createId(),
        closeOnChildrenClick = true,
        callable = true,
        alloweToClickClassesList = [],
        onClickOutside,
    } = props;
    const uniqueId = useRef(id);
    const [opened, setOpened] = useState(false);
    const menuRef: any = useRef();
    const toggleRef: any = useRef();
    const [direction, setDirection] = useState(`down`);
    useOnClickOutside(menuRef, (e: any) => {
        if (onClickOutside) {
            return onClickOutside(e);
        } else {
            const clickedOnAllowedClass = alloweToClickClassesList?.find?.((className: any) =>
                getElementClassPath(e?.target)
                    .filter((_i: any, j: number) => j > 0)
                    .find((i: any) => i?.includes?.(className)),
            );
            const isCurrentDropdown =
                (e.path || []).map((i: any) => i.className?.includes?.(uniqueId.current)).filter((i: any) => i)
                    .length === 0;
            if (isCurrentDropdown && !clickedOnAllowedClass) {
                if (opened) {
                    setOpened(false);
                }
            }
        }
    });
    useEffect(() => {
        return $$dropdowns.open.watch((incomingId) => {
            if (incomingId === id) {
                setOpened(true);
            }
        }).unsubscribe;
    }, []);
    useEffect(() => {
        return $$dropdowns.close.watch((incomingId) => {
            if (incomingId === id) {
                setOpened(false);
            }
        }).unsubscribe;
    }, []);
    useEffect(() => {
        setDirection(
            window?.outerHeight -
                (menuRef?.current?.getBoundingClientRect?.()?.y + window.scrollY) -
                toggleRef?.current?.clientHeight >
                menuRef?.current?.clientHeight
                ? `down`
                : `up`,
        );
    }, [menuRef, toggleRef]);
    const toggleSize = useComponentSize(toggleRef);
    const handleMenuItemClick = (e: any) => {
        if (
            getElementClassPath(e?.target)
                .filter((_i: any, j: number) => j > 1)
                .find((i: any) => i?.includes?.(`dropdown-menu`)) &&
            closeOnChildrenClick
        ) {
            setOpened(false);
        }
        e.stopPropagation();
    };
    const handleToggleClick = (event: any) => {
        event.stopPropagation();
        if (!callable) return;
        setOpened(!opened);
        setDirection(
            window?.outerHeight - event?.screenY - toggleRef?.current?.clientHeight > menuRef?.current?.clientHeight
                ? `down`
                : `up`,
        );
    };
    return (
        <DropdownStyles.Wrapper extra={wrapperStyles} className={`${className} ${uniqueId.current}`}>
            <DropdownStyles.Toggle
                {...toggleProps}
                className={`${toggleProps?.className ?? ``}`}
                ref={toggleRef}
                extra={(toggleProps?.extra ?? ``) + toggleStyles}
                onClick={handleToggleClick}
            >
                {toggle}
            </DropdownStyles.Toggle>
            <DropdownStyles.Menu
                {...menuProps}
                className={`${menuProps?.className ?? ``}`}
                ref={menuRef}
                visible={opened}
                toggleSize={toggleSize}
                onClick={handleMenuItemClick}
                direction={direction}
                extra={(menuProps?.extra ?? ``) + menuStyles}
            >
                {menu}
            </DropdownStyles.Menu>
        </DropdownStyles.Wrapper>
    );
};

export default Dropdown;
